import { useContext, useEffect, MouseEvent, ButtonHTMLAttributes } from 'react';
import classnames from 'classnames';
import useStyles from './Tab.styles';
import TabsContext from '../Tabs/TabsContext';
import '../themeDefaults';

const noop = () => null;

export interface ITabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /** enable dark mode for styling purposes */
  dark?: boolean;
  /** custom event handler for when a user clicks a tab */
  onChange?(event: MouseEvent<HTMLButtonElement>, value: string | number): void;
  /** currently selected tab */
  selected?: boolean;
  /** reference value for the given tab */
  value: string | number;
}

export function TabComponent({
  children,
  className,
  dark = false,
  onChange = noop,
  selected: propSelected,
  value,
  ...other
}: ITabProps) {
  const classes = useStyles();

  const { setValue, value: contextValue } = useContext(TabsContext);
  const selected = value === contextValue;

  useEffect(() => {
    if (!selected && propSelected) {
      setValue(value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onChange(event, value);
  };

  return (
    <button
      aria-selected={selected}
      className={classnames(
        // tab and classes.tab are 2 different things
        'tab',
        classes.tab,
        {
          [classes.darkTab]: dark,
          [dark ? classes.darkSelected : classes.selected]: selected,
        },
        className
      )}
      role="tab"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}
      onClick={handleChange}
      tabIndex={0}
      type="button"
    >
      {children}
    </button>
  );
}

const StyledTab = TabComponent;
TabComponent.displayName = 'Tab';

export default StyledTab;
